// Contracts of chess tables (chess, 国际象棋 — wave C of docs/four-games-mobile-plan.md, owner decision 2026-10-01
// «всё за токены»): a live game of two people for a stake (CHESS_STAKES 18 / 88 / 888), the winner takes the bank −
// CHESS_FEE_BPS, a draw returns both stakes, no first move → aborted (refund). Clock 5 + 3, authoritative on the server.
// Training against the bot is a skill-platform session (skill.ts, mode `training`), not a table.
//
//   GET /api/chess/lobby → ChessLobbyResponse          GET /api/chess/state → ChessTableView | ChessQueueView | null
//   socket (signed-in users): see ChessClientToServer / ChessServerToClient. The full view goes to `user:<id>` only.
//
// Races and repeats: every command carries the view's `ver` (the position version, +1 on every change of the game).
// A move with an old `ver` is NOT played — the answer is the current view (a resent command never makes a second move);
// `INVALID_MOVE { reason }` for an illegal move with the current `ver`, `NOT_YOUR_TURN` out of turn.
import { z } from 'zod';
import type { ApiError } from '../errors';
import { CHESS_STAKES } from '../catalog';
import { DDZ_CODE_ALPHABET } from './doudizhu';

export type ChessStakeValue = (typeof CHESS_STAKES)[number];
export const zChessStake = z.union([
  z.literal(CHESS_STAKES[0]),
  z.literal(CHESS_STAKES[1]),
  z.literal(CHESS_STAKES[2]),
]);

export type ChessSide = 'w' | 'b';

/** private table codes: the same alphabet as Dou Dizhu (no look-alikes) */
export const CHESS_CODE_ALPHABET = DDZ_CODE_ALPHABET;
export const zChessCode = z.string().regex(/^[ABCDEFGHJKMNPQRSTUVWXYZ2356789]{6}$/);

export const zChessUci = z.string().regex(/^[a-h][1-8][a-h][1-8][qrbn]?$/);

/**
 * A command of the player at the table:
 *   move    — a move in UCI (e2e4, e7e8q);
 *   resign  — gives up (before both first moves: the game is aborted, stakes refunded);
 *   offer   — offers a draw (one pending offer; again only after an own move);
 *   accept / decline — the opponent's offer;
 *   claim   — threefold repetition / fifty moves (the player to move; the server checks it).
 */
export const zChessAction = z.discriminatedUnion('type', [
  z.object({ type: z.literal('move'), uci: zChessUci }).strict(),
  z.object({ type: z.literal('resign') }).strict(),
  z.object({ type: z.literal('offer') }).strict(),
  z.object({ type: z.literal('accept') }).strict(),
  z.object({ type: z.literal('decline') }).strict(),
  z.object({ type: z.literal('claim') }).strict(),
]);
export type ChessAction = z.infer<typeof zChessAction>;
export const zChessActionInput = z
  .object({ gameId: z.string().uuid(), ver: z.number().int().min(0).max(1_000_000), action: zChessAction })
  .strict();
export type ChessActionRequest = z.infer<typeof zChessActionInput>;

export const CHESS_END_REASONS = [
  'checkmate',
  'stalemate',
  'dead',
  'fivefold',
  'seventyfive',
  'threefold',
  'fifty',
  'agreement',
  'resign',
  'timeout',
  'timeout-dead',
  'abort',
] as const;
export type ChessEndReasonName = (typeof CHESS_END_REASONS)[number];

export interface ChessPlayerView {
  publicId: string;
  nickname: string;
  avatar: string | null;
  /** a socket of this player has the table page open */
  connected: boolean;
}

export interface ChessResultView {
  /** null = a draw / an aborted game */
  winner: ChessSide | null;
  reason: ChessEndReasonName;
  /** per side: Bet.amount and what came back (stake + win − fee, the stake on a draw / abort, 0 for the loser) */
  stake: number;
  payout: { w: number; b: number };
  fee: number;
}

/** waiting — a private table waits for the friend; playing; ended — the result is shown until the table closes */
export type ChessPhase = 'waiting' | 'playing' | 'ended';

export interface ChessTableView {
  tableId: string;
  kind: 'quick' | 'private';
  stake: ChessStakeValue;
  feeBps: number;
  /** private table code (shared as a link /strategy/chess?code=…) */
  code: string | null;
  phase: ChessPhase;
  /** GameRound of the game (colours are drawn from its seed: hash shown at the start, seed after the end) */
  gameId: string | null;
  roundNo: number | null;
  serverSeedHash: string | null;
  serverSeed: string | null;
  /** position version: send it back with every command */
  ver: number;
  /** null — a spectator (never: tables have no spectators) / before seating */
  myColor: ChessSide | null;
  players: { w: ChessPlayerView | null; b: ChessPlayerView | null };
  fen: string;
  /** UCI and SAN of every move */
  moves: string[];
  san: string[];
  turn: ChessSide;
  /** the square of the king in check, or null */
  check: string | null;
  /** clock: ms left at `serverNow`, whose time runs (null before the clocks start / after the end) */
  clock: { w: number; b: number; running: ChessSide | null; incMs: number; baseMs: number };
  /** before both first moves: the moment the side to move must have moved (else the game is aborted) */
  firstMoveDeadline: number | null;
  /** the side that offers a draw now */
  drawOffer: ChessSide | null;
  /** the player may offer a draw now (one offer per own move) */
  canOffer: boolean;
  /** draws the player to move may claim now */
  claims: { threefold: boolean; fifty: boolean };
  result: ChessResultView | null;
  /** when an ended table closes (epoch ms) */
  closesAt: number | null;
  serverNow: number;
}

export interface ChessQueueView {
  stake: ChessStakeValue;
  queued: number;
  /** joined, epoch ms */
  since: number;
  serverNow: number;
}

export interface ChessLobbyStake {
  stake: ChessStakeValue;
  queued: number;
  /** games being played at this stake */
  tables: number;
}

export interface ChessLobbyResponse {
  stakes: ChessLobbyStake[];
  feeBps: number;
  baseMs: number;
  incMs: number;
  firstMoveMs: number;
}

export interface ChessServerToClient {
  'chess:table': (v: ChessTableView) => void;
  'chess:queue': (v: ChessQueueView) => void;
}

export interface ChessClientToServer {
  'chess:queue': (
    stake: ChessStakeValue,
    ack: (r: ChessQueueView | ChessTableView | ApiError) => void,
  ) => void;
  /** the queue at once; a table only when it is waiting (private) or ended — a game in progress: resign first */
  'chess:leave': (ack: (r: { ok: true } | ApiError) => void) => void;
  'chess:private:create': (stake: ChessStakeValue, ack: (r: ChessTableView | ApiError) => void) => void;
  /** TABLE_NOT_FOUND / TABLE_FULL / GAME_IN_PROGRESS / FORBIDDEN { reason: 'self' | 'same_network' } */
  'chess:private:join': (code: string, ack: (r: ChessTableView | ApiError) => void) => void;
  'chess:act': (input: ChessActionRequest, ack: (r: ChessTableView | ApiError) => void) => void;
  /** after a reconnect / reload: my table, my queue or nothing (never a new game) */
  'chess:resume': (ack: (r: ChessTableView | ChessQueueView | null | ApiError) => void) => void;
  /** the table page is open on this socket / was left (the opponent sees «disconnected»; the clock runs anyway) */
  'chess:present': (on: boolean) => void;
}

/* ---------------------------------------------------------------- admin: pairs report (anti-collusion) */

/**
 * GET /api/admin/analytics/chess-pairs?days=&min=&q= (admin) — pairs of players by games for a stake over the last
 * `days` days (aborted games aside) with the tokens that went between them: `aToB` = the stakes `a` lost to `b`
 * (the winner also pays the 5 % fee out of the bank), `net` = aToB − bToA (> 0: tokens flowed from a to b).
 * `q` — a part of a nickname (or the exact public id) of either player.
 */
export const CHESS_PAIRS_MAX_DAYS = 30;
export const zChessPairsQuery = z
  .object({
    days: z.coerce.number().int().min(1).max(CHESS_PAIRS_MAX_DAYS).default(7),
    min: z.coerce.number().int().min(1).max(1000).default(2),
    q: z.string().trim().max(40).optional(),
  })
  .strict();
export type ChessPairsQuery = z.input<typeof zChessPairsQuery>;

export interface ChessPairPlayer {
  id: string;
  publicId: number;
  nickname: string;
}

export interface ChessPairRow {
  a: ChessPairPlayer;
  b: ChessPairPlayer;
  games: number;
  /** games played today (UTC) — compare with `dailyLimit` */
  today: number;
  aWins: number;
  bWins: number;
  draws: number;
  /** stakes a lost to b / b lost to a */
  aToB: number;
  bToA: number;
  net: number;
  lastAt: string;
}

export interface ChessPairsReport {
  days: number;
  min: number;
  /** CHESS_PAIR_DAILY_LIMIT of the server */
  dailyLimit: number;
  rows: ChessPairRow[];
}
